import { useState, useEffect, useRef } from "react";
import { useApp } from "../../store.jsx";
import {
  Icon,
  Avatar,
  Pagination,
  EmptyState,
  SkeletonTable,
} from "../../components/Common.jsx";
import { fmt } from "../../data.js";
import {
  ReportModal,
  SubmitReportModal,
} from "../../components/Modals.jsx";
import { listReports, listDepartments, api, downloadReportsExport, emailReportsDigest } from "../../lib/api";
import html2pdf from "html2pdf.js";
import { useSession } from "../../lib/auth-client";

const parseBlockers = (b) => {
  if (!b) return [];
  if (Array.isArray(b)) return b;
  try {
    const v = JSON.parse(b);
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
};

// A scheme-less link ("drive.google.com/…") is otherwise treated as a relative
// path and opens our own site instead of the target — prepend https:// when no
// scheme is present.
const normalizeUrl = (u) =>
  /^[a-z][a-z0-9+.-]*:\/\//i.test(u) ? u : `https://${u}`;

// A report's `link` field can hold several URLs (employees paste multiple, e.g.
// Google Drive links). Split on whitespace/commas so each opens individually.
const splitLinks = (raw) => {
  if (!raw) return [];
  return String(raw)
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map(normalizeUrl);
};

export default function Reports() {
  const { openModal, closeModal, toast } = useApp();
  const { data: session } = useSession();
  const role = session?.user?.role || "employee";
  const canSubmitReport = role === "employee" || role === "manager";
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [serverTotal, setServerTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("today");
  const [sort, setSort] = useState("date");
  const [dir, setDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [deptFilter, setDeptFilter] = useState("");
  const [departments, setDepartments] = useState([]);
  const searchTimerRef = useRef(null);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [exportOpen, setExportOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const toggleSelect = (id) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  const toggleSelectAll = () => {
    if (reports.length > 0 && reports.every((r) => selectedIds.includes(r.id))) {
      setSelectedIds([]);
    } else {
      setSelectedIds(reports.map((r) => r.id));
    }
  };
  const clearSelection = () => setSelectedIds([]);
  const isSelected = (id) => selectedIds.includes(id);
  const selectedCount = selectedIds.length;

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(val);
      setPage(1);
    }, 300);
  };

  const refresh = async () => {
    setLoading(true);
    setSelectedIds([]); // selection is scoped to the visible page; reset on reload
    try {
      const data = await listReports({
        from: dateFrom || undefined,
        to: dateTo || undefined,
        search: debouncedSearch || undefined,
        filter,
        dept: deptFilter || undefined,
        page,
        pageSize,
        sort,
        dir,
      });
      setReports(data?.reports || []);
      setServerTotal(data?.total || 0);
    } catch (err) {
      toast(err.message || "Could not load reports", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, [dateFrom, dateTo, debouncedSearch, filter, deptFilter, page, pageSize, sort, dir]); // eslint-disable-line

  useEffect(() => {
    listDepartments().then((res) => setDepartments(res?.departments || res || [])).catch(() => {});
  }, []);

  const total = serverTotal;
  const start = (page - 1) * pageSize;
  const paged = reports;
  const pages = Math.max(1, Math.ceil(total / pageSize));

  const sortBy = (col) => {
    if (sort === col) setDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSort(col);
      setDir("asc");
    }
  };

  // Adapter to feed the existing ReportModal which expects the older mock shape.
  const view = (r) =>
    openModal(
      <ReportModal
        report={{
          ...r,
          employee: r.user,
          tasks: (r.reportTasks || []).length
            ? r.reportTasks.map((rt) => ({
                taskId: rt.taskId, // needed to edit per-task status
                text: rt.task?.title || "(task)",
                status: rt.status,
                note: rt.note,
                progressPct: rt.progressPct ?? null,
                source: rt.task?.source,
                carried: !!rt.carriedFrom,
                assignedAt: rt.task?.createdAt, // when the task was assigned/added
                submittedAt: rt.createdAt, // when this task's status was reported
              }))
            : [{ text: r.message || "", status: "done" }],
          blockers: (r.reportTasks || [])
            .filter((rt) => rt.status === "pending" && rt.note)
            .map((rt) => rt.note),
          aiSummary: r.analysis?.summary || "AI summary pending...",
          sentiment: r.analysis?.sentiment || "neutral",
          source: r.source || "web",
          hoursWorked: 0,
        }}
        onClose={closeModal}
        onSaved={() => {
          closeModal();
          refresh();
        }}
      />,
      "lg",
    );

  const exportAll = async () => {
    try {
      toast("Preparing export…", "info");
      await downloadReportsExport({ scope: "all", filter, from: dateFrom || undefined, to: dateTo || undefined, dept: deptFilter || undefined });
      toast(
        filter === "all"
          ? "All reports exported"
          : `Exported (${filter})`,
        "success",
      );
    } catch (e) {
      toast(e.message || "Export failed", "error");
    }
  };

  // Client-side CSV of the currently-selected rows — we already hold the report
  // objects, so no server round trip is needed (mirrors the Employees table).
  const exportSelectedCSV = () => {
    const selected = reports.filter((r) => selectedIds.includes(r.id));
    if (!selected.length) return;
    const esc = (v) => {
      if (v == null) return "";
      const s = String(v);
      return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = ["Employee", "Date", "Submitted", "Source", "Summary", "Sentiment", "Productivity", "Urgency", "Blockers", "Link"];
    const rows = selected.map((r) => {
      const a = r.analysis || {};
      return [
        r.user?.name || "",
        fmt.date(r.date),
        r.createdAt ? fmt.dateTime(r.createdAt) : "",
        r.source === "whatsapp" ? "WhatsApp" : "Web",
        a.summary || r.message || "",
        a.sentiment || "",
        typeof a.productivity === "number" ? `${a.productivity}%` : "",
        a.urgency || "",
        parseBlockers(a.blockers).join(" | "),
        r.link || "",
      ].map(esc).join(",");
    });
    const csv = "﻿" + [header.map(esc).join(","), ...rows].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `selected-reports-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast(`Exported ${selected.length} report(s)`, "success");
  };

  // rowsOverride: when given (selected rows), render just those instead of
  // fetching the full filtered dataset from the server.
  const exportPDF = async (rowsOverride) => {
    let rows = rowsOverride
      ? rowsOverride.filter((r) => r.analysis?.summary || r.message)
      : null;
    if (!rows) {
      try {
        const all = await listReports({
          from: dateFrom || undefined,
          to: dateTo || undefined,
          search: debouncedSearch || undefined,
          filter,
          dept: deptFilter || undefined,
          page: 1,
          pageSize: 10000,
          sort,
          dir,
        });
        rows = (all?.reports || []).filter((r) => r.analysis?.summary || r.message);
      } catch {
        toast("Failed to load all reports for PDF", "error");
        return;
      }
    }
    if (!rows.length) { toast("No reports to export", "error"); return; }
    const title = `Daily-Reports-${rowsOverride ? "selected" : filter === "today" ? new Date().toISOString().slice(0, 10) : filter}`;
    const el = document.createElement("div");
    el.innerHTML = `
      <div style="font-family:Inter,system-ui,sans-serif;padding:40px;color:#1a1a2e;font-size:13px">
        <h1 style="font-size:22px;margin-bottom:4px">${title.replace(/-/g, " ")}</h1>
        <p style="color:#6b7280;font-size:13px;margin-bottom:24px">${rows.length} report${rows.length === 1 ? "" : "s"} · ${new Date().toLocaleString()}</p>
        <table style="width:100%;border-collapse:collapse">
          <thead>
            <tr>
              <th style="text-align:left;padding:8px 10px;background:#f3f4f6;font-size:10px;text-transform:uppercase;letter-spacing:.05em;color:#6b7280;border-bottom:2px solid #e5e7eb">Employee</th>
              <th style="text-align:left;padding:8px 10px;background:#f3f4f6;font-size:10px;text-transform:uppercase;letter-spacing:.05em;color:#6b7280;border-bottom:2px solid #e5e7eb">Date</th>
              <th style="text-align:left;padding:8px 10px;background:#f3f4f6;font-size:10px;text-transform:uppercase;letter-spacing:.05em;color:#6b7280;border-bottom:2px solid #e5e7eb">Source</th>
              <th style="text-align:left;padding:8px 10px;background:#f3f4f6;font-size:10px;text-transform:uppercase;letter-spacing:.05em;color:#6b7280;border-bottom:2px solid #e5e7eb">Summary</th>
              <th style="text-align:left;padding:8px 10px;background:#f3f4f6;font-size:10px;text-transform:uppercase;letter-spacing:.05em;color:#6b7280;border-bottom:2px solid #e5e7eb">Sentiment</th>
              <th style="text-align:left;padding:8px 10px;background:#f3f4f6;font-size:10px;text-transform:uppercase;letter-spacing:.05em;color:#6b7280;border-bottom:2px solid #e5e7eb">Productivity</th>
            </tr>
          </thead>
          <tbody>
            ${rows.map((r) => {
              const u = r.user || {};
              const a = r.analysis || {};
              const s = a.sentiment || "neutral";
              const prod = typeof a.productivity === "number" ? a.productivity + "%" : "—";
              const src = r.source === "whatsapp" ? "WhatsApp" : "Web";
              const summary = a.summary || r.message || "";
              const bg = s === "positive" ? "#d1fae5" : s === "negative" ? "#fee2e2" : "#f3f4f6";
              const color = s === "positive" ? "#059669" : s === "negative" ? "#dc2626" : "#6b7280";
              return `<tr>
                <td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;font-size:12.5px"><strong>${u.name || "?"}</strong></td>
                <td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;font-size:12.5px">${fmt.date(r.date)}</td>
                <td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;font-size:12.5px">${src}</td>
                <td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;font-size:12.5px;max-width:320px">${summary.slice(0, 200)}</td>
                <td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;font-size:12.5px"><span style="display:inline-block;padding:2px 8px;border-radius:999px;font-size:10px;font-weight:600;background:${bg};color:${color}">${s}</span></td>
                <td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;font-size:12.5px">${prod}</td>
              </tr>`;
            }).join("")}
          </tbody>
        </table>
      </div>`;
    document.body.appendChild(el);
    try {
      await html2pdf().from(el).set({ filename: `${title}.pdf`, image: { type: "jpeg", quality: 0.95 }, html2canvas: { scale: 2, useCORS: true }, jsPDF: { unit: "mm", format: "a4", orientation: "landscape" } }).save();
    } catch (e) {
      toast(e.message || "PDF export failed", "error");
    } finally {
      document.body.removeChild(el);
    }
  };

  // The backend kicks off AI analysis asynchronously and returns the report
  // before /analyze finishes. Refresh the list, then poll the new row for up
  // to ~6 seconds so the AI fields appear without the user needing to reload.
  const pollForAnalysis = async (reportId) => {
    for (let i = 0; i < 3; i++) {
      await new Promise((r) => setTimeout(r, 2000));
      try {
        const { report } = await api.get(`/api/reports/${reportId}`);
        if (report?.analysis) {
          setReports((rs) => rs.map((r) => (r.id === report.id ? report : r)));
          return;
        }
      } catch {
        /* transient — user can refresh */
      }
    }
  };

  const openNew = () =>
    openModal(
      <SubmitReportModal
        onClose={closeModal}
        onSubmit={async (result) => {
          toast("Report submitted", "success");
          await refresh();
          if (result?.report?.id) pollForAnalysis(result.report.id);
        }}
      />,
      "md",
    );

  const [sending, setSending] = useState(false);
  const sendDigest = async () => {
    if (sending) return;
    setSending(true);
    try {
      toast("Preparing digest…", "info");
      const r = await emailReportsDigest(
        filter,
        selectedCount > 0 ? selectedIds : undefined,
      );
      toast(
        `Digest sent to ${r.sentTo} — ${r.count} report${r.count === 1 ? "" : "s"}. Check your inbox.`,
        "success",
      );
    } catch (e) {
      toast(e.message || "Could not send digest", "error");
    } finally {
      setSending(false);
    }
  };

  return (
    <section>
      <div className="page-head">
        <div>
          <h1 className="page-title">Daily Reports</h1>
          <p className="page-sub">
            {total} report{total === 1 ? "" : "s"}{filter === "today" ? " today" : filter === "week" ? " this week" : filter === "whatsapp" ? " via WhatsApp" : filter === "web" ? " via Web" : filter === "blockers" ? " with blockers" : ""} {loading ? "· loading..." : ""}
          </p>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          {selectedCount > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 600, whiteSpace: "nowrap" }}>
                {selectedCount} selected
              </span>
              <button
                className="btn btn-ghost btn-sm"
                style={{ padding: "2px 8px" }}
                onClick={clearSelection}
                title="Clear selection"
              >
                <Icon name="x" size={14} />
              </button>
            </div>
          )}
          <div style={{ position: "relative" }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setExportOpen((o) => !o)} onBlur={() => setTimeout(() => setExportOpen(false), 200)}>
              <Icon name="download" size={16} /> Export{selectedCount > 0 ? ` (${selectedCount})` : ""} <Icon name="chevron-down" size={12} />
            </button>
            {exportOpen && (
              <div style={{ position: "absolute", top: "100%", right: 0, marginTop: 4, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,.12)", zIndex: 50, minWidth: 220, overflow: "hidden" }}>
                <button className="btn btn-ghost btn-sm" style={{ width: "100%", justifyContent: "flex-start", borderRadius: 0, padding: "8px 14px" }} onClick={() => { (selectedCount > 0 ? exportSelectedCSV() : exportAll()); setExportOpen(false); }}><Icon name="file-text" size={14} /> {selectedCount > 0 ? `Export ${selectedCount} selected as CSV` : "Export as CSV"}</button>
                <button className="btn btn-ghost btn-sm" style={{ width: "100%", justifyContent: "flex-start", borderRadius: 0, padding: "8px 14px", borderTop: "1px solid var(--border)" }} onClick={() => { (selectedCount > 0 ? exportPDF(reports.filter((r) => selectedIds.includes(r.id))) : exportPDF()); setExportOpen(false); }}><Icon name="file" size={14} /> {selectedCount > 0 ? `Export ${selectedCount} selected as PDF` : "Export as PDF"}</button>
              </div>
            )}
          </div>
          <button
            className="btn btn-ghost btn-sm"
            onClick={sendDigest}
            disabled={sending}
            title={selectedCount > 0 ? "Email the selected reports to my inbox" : "Email the current filter to my inbox"}
          >
            <Icon name="mail" size={16} /> {sending ? "Sending…" : selectedCount > 0 ? `Send me (${selectedCount})` : "Send me"}
          </button>
          {canSubmitReport && (
            <button className="btn btn-primary btn-sm" onClick={openNew}>
              <Icon name="plus" size={16} /> New Report
            </button>
          )}
        </div>
      </div>
      <div className="table-toolbar">
        <div className="toolbar-left">
          <div className="toolbar-search">
            <Icon name="search" size={14} />
            <input
              type="text"
              placeholder="Search reports..."
              value={search}
              onChange={handleSearchChange}
            />
          </div>
          <select
            className="input"
            value={deptFilter}
            onChange={(e) => { setDeptFilter(e.target.value); setPage(1); }}
            style={{ width: 160, fontSize: 13, padding: "4px 8px", border: "1px solid var(--border)", borderRadius: 6, background: "var(--surface)", color: "var(--text)" }}
          >
            <option value="">All departments</option>
            {departments.map((d) => (
              <option key={d.id || d.name} value={d.name}>{d.name}</option>
            ))}
          </select>
          <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setFilter("all"); setPage(1); }}
            style={{ fontSize: 12, padding: "4px 8px", border: "1px solid var(--border)", borderRadius: 6, background: "var(--surface)", color: "var(--text)" }} />
          <span style={{ fontSize: 12, color: "var(--text-muted)" }}>to</span>
          <input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setFilter("all"); setPage(1); }}
            style={{ fontSize: 12, padding: "4px 8px", border: "1px solid var(--border)", borderRadius: 6, background: "var(--surface)", color: "var(--text)" }} />
          {(dateFrom || dateTo) && (
            <button className="btn btn-ghost btn-sm" style={{ fontSize: 11, padding: "2px 8px" }} onClick={() => { setDateFrom(""); setDateTo(""); setFilter("today"); setPage(1); }}>Clear</button>
          )}
        </div>
      </div>
      <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
        {[
          ["all", "All"],
          ["today", "Today"],
          ["week", "This week"],
          ["whatsapp", "WhatsApp only"],
          ["web", "Web only"],
          ["blockers", "With blockers"],
        ].map(([k, l]) => (
          <button
            key={k}
            className={`filter-chip ${filter === k ? "active" : ""}`}
            onClick={() => { setFilter(k); setPage(1); }}
          >
            {l}
          </button>
        ))}
      </div>
      {!dateFrom && !dateTo && (
        <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 8 }}>
          Showing last 90 days. Use the date range filter or Export to access older data.
        </div>
      )}
      {paged.length ? (
        <div className="table-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ width: 40 }}>
                  <input
                    type="checkbox"
                    checked={paged.length > 0 && paged.every((r) => isSelected(r.id))}
                    onChange={toggleSelectAll}
                    aria-label="Select all reports on this page"
                  />
                </th>
                <th onClick={() => sortBy("employee")}>
                  Employee{" "}
                  <Icon
                    name="chevrons-up-down"
                    size={11}
                    className="sort-icon"
                  />
                </th>
                <th onClick={() => sortBy("date")}>
                  Date{" "}
                  <Icon
                    name="chevrons-up-down"
                    size={11}
                    className="sort-icon"
                  />
                </th>
                <th onClick={() => sortBy("createdAt")}>
                  Submitted{" "}
                  <Icon
                    name="chevrons-up-down"
                    size={11}
                    className="sort-icon"
                  />
                </th>
                <th>Message / Summary</th>
                <th>Link</th>
                <th>Source</th>
                <th>Sentiment</th>
                <th>Productivity</th>
                <th>Urgency</th>
                <th>Blockers</th>
                <th style={{ width: 60 }}></th>
              </tr>
            </thead>
            <tbody>
              {paged.map((r) => {
                const u = r.user || {};
                const a = r.analysis;
                const s = a?.sentiment;
                const blockers = parseBlockers(a?.blockers);
                const ageMs =
                  Date.now() - new Date(r.createdAt || r.date).getTime();
                const analyzing = !a && ageMs < 60_000;
                const prod =
                  typeof a?.productivity === "number" ? a.productivity : null;
                const urg = a?.urgency;
                return (
                  <tr key={r.id} className={isSelected(r.id) ? "row-selected" : ""}>
                    <td className="card-hide">
                      <input
                        type="checkbox"
                        checked={isSelected(r.id)}
                        onChange={() => toggleSelect(r.id)}
                        aria-label={`Select report by ${u.name || "employee"}`}
                      />
                    </td>
                    <td data-label="Employee">
                      <div className="cell-emp">
                        <Avatar
                          name={u.name || "?"}
                          size="sm"
                          seed={(u.id || "").length}
                        />
                        <div>
                          <div className="name">{u.name || "Unknown"}</div>
                        </div>
                      </div>
                    </td>
                    <td data-label="Date">{fmt.date(r.date)}</td>
                    <td data-label="Submitted">
                      {r.createdAt ? (
                        <span title={fmt.dateTime(r.createdAt)}>
                          {fmt.time(r.createdAt)}
                        </span>
                      ) : (
                        <span style={{ color: "var(--text-muted)" }}>—</span>
                      )}
                    </td>
                    <td data-label="Summary" style={{ maxWidth: 360, whiteSpace: "pre-wrap" }}>
                      <div>
                        {(r.message || "").slice(0, 180)}
                        {(r.message || "").length > 180 ? "…" : ""}
                      </div>
                      {a?.summary && (
                        <div
                          style={{
                            marginTop: 4,
                            fontSize: 12,
                            color: "var(--text-muted)",
                            fontStyle: "italic",
                          }}
                        >
                          AI: {a.summary}
                        </div>
                      )}
                      {analyzing && (
                        <div
                          style={{
                            marginTop: 4,
                            fontSize: 12,
                            color: "var(--text-muted)",
                          }}
                        >
                          <Icon name="loader" size={11} /> Analyzing…
                        </div>
                      )}
                    </td>
                    <td data-label="Link">
                      {(() => {
                        const links = splitLinks(r.link);
                        if (!links.length)
                          return <span style={{ color: "var(--text-muted)" }}>—</span>;
                        if (links.length === 1)
                          return (
                            <a
                              href={links[0]}
                              target="_blank"
                              rel="noreferrer"
                              style={{ color: "var(--primary-2)" }}
                            >
                              Open ↗
                            </a>
                          );
                        return (
                          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            {links.map((u, i) => (
                              <a
                                key={i}
                                href={u}
                                target="_blank"
                                rel="noreferrer"
                                style={{ color: "var(--primary-2)", whiteSpace: "nowrap" }}
                              >
                                Open {i + 1} ↗
                              </a>
                            ))}
                          </div>
                        );
                      })()}
                    </td>
                    <td data-label="Source">
                      {r.source === "whatsapp" ? (
                        <span
                          className="badge"
                          style={{
                            background: "rgba(37,211,102,.15)",
                            color: "#25D366",
                          }}
                        >
                          <Icon name="message-circle" size={11} /> WhatsApp
                        </span>
                      ) : (
                        <span className="badge badge-muted">
                          <Icon name="globe" size={11} /> Web
                        </span>
                      )}
                    </td>
                    <td data-label="Sentiment">
                      {s === "positive" ? (
                        <span className="badge badge-success">
                          <span className="badge-dot" /> Positive
                        </span>
                      ) : s === "negative" ? (
                        <span className="badge badge-error">
                          <span className="badge-dot" /> At risk
                        </span>
                      ) : s === "neutral" ? (
                        <span className="badge badge-muted">
                          <span className="badge-dot" /> Neutral
                        </span>
                      ) : (
                        <span style={{ color: "var(--text-muted)" }}>—</span>
                      )}
                    </td>
                    <td data-label="Productivity">
                      {prod !== null ? (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            minWidth: 110,
                          }}
                        >
                          <div
                            style={{
                              flex: 1,
                              height: 6,
                              background: "var(--surface-3)",
                              borderRadius: 3,
                              overflow: "hidden",
                              maxWidth: 80,
                            }}
                          >
                            <div
                              style={{
                                height: "100%",
                                width: prod + "%",
                                background:
                                  prod >= 80
                                    ? "var(--success)"
                                    : prod >= 60
                                      ? "var(--warning)"
                                      : "var(--error)",
                              }}
                            />
                          </div>
                          <span style={{ fontWeight: 600, fontSize: 12.5 }}>
                            {prod}%
                          </span>
                        </div>
                      ) : (
                        <span style={{ color: "var(--text-muted)" }}>—</span>
                      )}
                    </td>
                    <td data-label="Urgency">
                      {urg === "high" ? (
                        <span className="badge badge-error">High</span>
                      ) : urg === "medium" ? (
                        <span
                          className="badge"
                          style={{
                            background: "rgba(245,158,11,.15)",
                            color: "#F59E0B",
                          }}
                        >
                          Medium
                        </span>
                      ) : urg === "low" ? (
                        <span className="badge badge-muted">Low</span>
                      ) : (
                        <span style={{ color: "var(--text-muted)" }}>—</span>
                      )}
                    </td>
                    <td data-label="Blockers">
                      {blockers.length > 0 ? (
                        <span
                          className="badge"
                          style={{
                            background: "rgba(239,68,68,.12)",
                            color: "#F87171",
                          }}
                        >
                          {blockers.length}
                        </span>
                      ) : (
                        <span style={{ color: "var(--text-muted)" }}>—</span>
                      )}
                    </td>
                    <td data-label="">
                      <div className="row-actions">
                        <button
                          className="icon-btn"
                          title="View"
                          onClick={() => view(r)}
                        >
                          <Icon name="eye" size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <Pagination
            page={page}
            pages={pages}
            total={total}
            from={start}
            to={Math.min(start + pageSize, total)}
            pageSize={pageSize}
            onPage={setPage}
            onPageSize={(s) => {
              setPageSize(s);
              setPage(1);
            }}
          />
        </div>
      ) : loading ? (
        <SkeletonTable rows={6} cols={6} />
      ) : (
        <EmptyState
          title="No reports found"
          desc={
            canSubmitReport
              ? "Submit your first report to get started."
              : "No reports have been submitted yet."
          }
          icon="file-text"
          cta={canSubmitReport ? "New Report" : undefined}
          onCta={canSubmitReport ? openNew : undefined}
        />
      )}
    </section>
  );
}
